<template>
  <div class="page users">
    <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
    <SuccessAlert :message="successMessage" @clear-error="clearMessage" />

    <PageHeader
      title="Utilizatori"
      subtitle="Conturile, rolurile și departamentele fiecărei persoane."
      icon="mdi-account-group-outline"
    >
      <template #actions>
        <UserForm ref="userForm" @updateUserData="fetchUserData" />
      </template>
    </PageHeader>

    <div class="page-body">
      <section class="panel">
        <div class="panel-toolbar">
          <h2 class="panel-title">
            Toate conturile
            <span class="count">{{ filteredData.length }}</span>
          </h2>
          <span class="grow-spacer" />
          <SearchBar @search="handleSearch" />
          <ColumnSelect :headers="headers" :updateVisibleColumns="updateVisibleColumns" pref-key="users" />
        </div>
        <v-data-table
          :mobile-breakpoint="0"
          :headers="visibleHeaders"
          :items="filteredData"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :loading="isLoading"
          @pagination="pageStart = $event.pageStart"
          loading-text="Se încarcă utilizatorii..."
          no-data-text="Nu există utilizatori"
        >
          <template v-slot:item="{ item, index }">
            <tr :class="{ 'light-green-row': index % 2 !== 0, 'row-blocked': item.isActive === 'Blocat' }">
              <td
                v-for="(header, hIndex) in visibleHeaders"
                :key="hIndex"
                :style="header.style ? header.style : {}"
              >
                <div v-if="header.value === 'actions'" class="nowrap-icons">
                  <v-btn icon aria-label="Vezi" @click="viewItem(item)"><v-icon>mdi-eye-outline</v-icon></v-btn>
                  <v-btn icon color="primary" aria-label="Editează" @click="editItem(item)"><v-icon>mdi-pencil-outline</v-icon></v-btn>
                  <v-btn icon color="error" aria-label="Șterge" @click="deleteItem(item)"><v-icon>mdi-delete-outline</v-icon></v-btn>
                </div>
                <div v-else-if="header.value === 'department'" class="dept-cell">
                  <span v-for="d in item.departments" :key="d.name" class="dept-cell__item">
                    {{ d.name }}<span v-if="d.isSef" class="tag tag--sef">Șef</span>
                  </span>
                  <span v-if="!item.departments || !item.departments.length">{{ item.department }}</span>
                </div>
                <span v-else-if="header.value === 'isActive'" class="state" :class="item.isActive === 'Activ' ? 'state--on' : 'state--off'">
                  {{ item.isActive }}
                </span>
                <template v-else>
                  {{ getTableCellValue(item, header, index) }}
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </section>
    </div>

    <EditUserPopup
      ref="editUserPopup"
      :user="selectedUser"
      :visible="showEditPopup"
      @updateUserData="fetchUserData"
    />
    <DeleteItem
      ref="deleteItem"
      :user="selectedUser"
      :visible="dialogDelete"
      @updateUserData="fetchUserData"
    />
    <ViewItem
      ref="viewItem"
      :user="selectedUser"
      :visible="dialogDelete"
      @updateUserData="fetchUserData"
    />
  </div>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import PageHeader from "@/components/layout/PageHeader.vue";
import { roleLabel } from "@/config/roles";
import ColumnSelect from "@/components/elements/ColumnSelect.vue";
import SearchBar from "@/components/elements/SearchBar.vue";
import UserForm from "./UserForm.vue";
import EditUserPopup from "./EditUserPopup.vue";
import DeleteItem from "./DeleteItem.vue";
import ViewItem from "./ViewItem.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";

export default {
  name: "UsersPage",
  components: {
    UserForm,
    EditUserPopup,
    DeleteItem,
    PageHeader,
    ErrorAlert,
    ColumnSelect,
    SearchBar,
    SuccessAlert,
    ViewItem,
  },
  data() {
    return {
      headers: [],
      userData: [],
      visibleHeaders: [],
      selectedUser: null,
      showEditPopup: false,
      dialogDelete: false,
      search: "",
      errorMessage: "",
      successMessage: "",
      sortBy: "id",
      sortDesc: false,
      isLoading: true,
      // first row of the current table page, so "Nr" continues across pages
      pageStart: 0,
    };
  },
  methods: {
    async fetchUserData() {
      try {
        const rows = await api.getUsersTableData();
        // flat text so the search finds people by any of their departments and by role name
        this.userData = rows.map((row) => ({
          ...row,
          roleText: roleLabel(row.roleId && row.roleId.role),
          departmentsText: (row.departments || []).map((d) => d.name).join(", "),
        }));
        this.isLoading = false;
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    viewItem(item) {
      this.selectedUser = { ...item };
      this.$refs.viewItem.show();
    },
    editItem(item) {
    // console.log("Selected user to edit:", item); // Add this log to check the data
    this.selectedUser = { ...item };
    this.$refs.editUserPopup.show();
  },
    deleteItem(item) {
      this.selectedUser = { ...item };
      this.$refs.deleteItem.show();
    },
    handleError(message) {
      this.errorMessage = message;
    },
    handleSuccess(message) {
      this.successMessage = message;
    },
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    handleSearch(searchTerm) {
      this.search = searchTerm;
    },
    getTableCellValue(item, header, index) {
      if (header.value === "index") return this.pageStart + index + 1;
      if (header.value === "full_name") return item.full_name;
      if (header.value === "user_name") return item.user_name;
      if (header.value === "role") return item.roleText;
      if (header.value === "isActive") return item.isActive;
      if (header.value === "idnp") return item.idnp !== "" ? item.idnp : "";
      if (header.value === "email") return item.email;
      if (header.value === "department") return item.department;
      if (header.value === "telephone") return item.telephone || "Nu există"; 
      if (header.value === "landline") return item.landline || "Nu există";
      return "";
    },
    sortTable(header) {
      const result = helper.sortTable(header);
      return result;
    },
    updateVisibleColumns() {
      this.visibleHeaders = this.headers.filter((header) => header.visible);
    },
  },

  computed: {
    filteredData() {
      const result = helper.filteredData(this.userData, this.search);
      return result;
    },
  },
  mounted() {
    this.headers = require("./users.json");
    this.visibleHeaders = this.headers.filter(
      (header) => header.defaultVisible
    );
    this.headers.forEach((header) => {
      header.visible = header.defaultVisible;
    });
    this.fetchUserData();
    this.$refs.deleteItem.$on("error", this.handleError);
    this.$refs.deleteItem.$on("success", this.handleSuccess);
    this.$refs.editUserPopup.$on("error", this.handleError);
    this.$refs.editUserPopup.$on("success", this.handleSuccess);
    this.$refs.userForm.$on("error", this.handleError);
    this.$refs.userForm.$on("success", this.handleSuccess);
    this.$refs.viewItem.$on("error", this.handleError);
  },
};
</script>

<style scoped>
.nowrap-icons {
  white-space: nowrap;
}
.dept-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 0;
}
.dept-cell__item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.tag {
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}
.tag--sef {
  background: var(--pine-900);
  color: var(--leaf);
}
.state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
}
.state::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.state--on {
  color: var(--green-700);
}
.state--off {
  color: var(--brick);
}
.row-blocked > td {
  color: var(--ink-3);
}
</style>
